import { CacheControl } from '@tusbar/cache-control'

import { ApolloClientProfile, Client } from '@syconium/weeping-figs'

import { NotFoundPage } from '../../../../app/_not-found.client'
import { QuickBuyModal } from '../../../../brunswick/components/QuickBuyModal'
import { QuickBuyContainer } from '../../../../brunswick/containers/QuickBuyContainer'
import {
  CONTENT_KEY,
  INCLUDE_UNAVAILABLE_PRODUCTS,
  PREVIEW,
  PRODUCT_DATA_KEY,
} from '../../../../constants'
import { FETCH_KIT, FETCH_KIT_PRODUCT } from '../../../../lib/graphql/queries'
import { cachePolicies } from '../../../../lib/response-cache'
import { singleQueryParamValue } from '../../../../lib/utils'
import {
  FetchKitContentResponse,
  FetchKitProductResponse,
  KitContent,
  Kit as KitType,
} from '../../../../types/graphql'
import { MagnoliaPage, MagnoliaPageInitialProps } from '../../MagnoliaApp'

import { KitPageMain } from './KitPageMain'
import { KitPageMeta } from './KitPageMeta'

type KitPageProps = MagnoliaPageInitialProps<{
  errors: ReadonlyArray<Error>
  handle: string
  kit: KitType | null
  kitContent: KitContent | null
}>

export const KitPage: MagnoliaPage<KitPageProps> = ({ errors, handle, kit, kitContent }) => {
  const kitProduct = kit?.productGroup?.products[0]
  const images = kitProduct?.images?.nodes || []
  const coloredCatwalkVideo = kitProduct?.videos.nodes.find(video => video.kind === 'catwalk')
  const videos = !!coloredCatwalkVideo ? [coloredCatwalkVideo] : []
  const { description, noindex, title } = kitProduct ?? {}
  const seoDescription = kit?.productGroup.seoDescription || ''
  const seoTitle = kit?.productGroup.seoTitle

  if (!kit) return <NotFoundPage />

  // TODO: Errors should be handled here generically, and not passed to KitPageMain
  // KitPageMain should be rendered only when things are going well.
  return (
    <QuickBuyContainer.Provider>
      <KitPageMeta
        description={description}
        handle={handle}
        noindex={noindex}
        seoDescription={seoDescription}
        seoTitle={seoTitle}
        title={title}
        image={images[0]?.source}
        video={videos[0]?.source}
      />
      <KitPageMain errors={errors} handle={handle} kit={kit} kitContent={kitContent} />
      <QuickBuyModal />
    </QuickBuyContainer.Provider>
  )
}

interface QueryOptions {
  includeUnavailable: boolean
  isPreview: boolean
  launchTags: string
}

KitPage.getInitialProps = async function (pageContext) {
  const handle: string = singleQueryParamValue(pageContext.query.handle, '')
  const productDataParams = singleQueryParamValue(pageContext.query[PRODUCT_DATA_KEY], '')
  const includeUnavailable: boolean = productDataParams === INCLUDE_UNAVAILABLE_PRODUCTS
  const launchTags: string = includeUnavailable ? '' : productDataParams
  const isPreview: boolean = pageContext.query[CONTENT_KEY] === PREVIEW
  const figsPublicClient = pageContext.apolloClientFactory.for(
    ApolloClientProfile.FigsPublicSupergraph
  )
  const contentClient = pageContext.apolloClientFactory.for(
    ApolloClientProfile.FigsPublicSupergraph
  )
  const queryOptions: QueryOptions = {
    includeUnavailable,
    isPreview,
    launchTags,
  }
  const [kitProduct, kitContent] = await Promise.all([
    fetchKitProduct(figsPublicClient, handle, queryOptions),
    fetchKitContent(contentClient, handle, queryOptions),
  ])

  if (
    (!kitProduct ||
      !kitProduct.data ||
      !kitProduct.data.productKit ||
      !kitContent ||
      !kitContent.data ||
      !kitContent.data.kit) &&
    pageContext.res
  ) {
    pageContext.res.statusCode = 404
  }
  const errors = [
    ...(kitProduct.error ? [kitProduct.error] : []),
    ...(kitProduct.errors ?? []),
    ...(kitContent.error ? [kitContent.error] : []),
    ...(kitContent.errors ?? []),
  ]
  return {
    errors,
    handle,
    kit: kitProduct.data?.productKit ?? null,
    kitContent: kitContent.data?.kit ?? null,
    responseCacheControl: cachePolicy(errors, includeUnavailable, isPreview),
  }
}

function fetchKitProduct(
  client: Client,
  handle: string,
  { includeUnavailable, isPreview, launchTags }: QueryOptions
) {
  return client.query<FetchKitProductResponse>({
    query: FETCH_KIT_PRODUCT,
    variables: { handle },
    context: {
      isPreview,
      includeUnavailable,
      launchTags,
    },
  })
}

function fetchKitContent(
  client: Client,
  handle: string,
  { includeUnavailable, isPreview, launchTags }: QueryOptions
) {
  return client.query<FetchKitContentResponse>({
    query: FETCH_KIT,
    variables: { handle },
    context: {
      includeUnavailable,
      isPreview,
      launchTags,
    },
  })
}

function cachePolicy(
  errors: ReadonlyArray<Error>,
  includeUnavailable: boolean,
  isPreview: boolean
): CacheControl {
  return includeUnavailable || isPreview || errors.length > 0
    ? cachePolicies.noCache
    : cachePolicies.publicHtmlPolicy
}

export default KitPage
